// client/src/pages/ShopPOS/components/ProductGrid.tsx
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, X, RefreshCw } from "lucide-react";
import { ProductCard } from "./ProductCard";

interface ProductGridProps {
  filteredInventory: any[];
  products: any[];
  search: string;
  minPrice: number | "";
  maxPrice: number | "";
  isLoading: boolean;
  inventoryLoading: boolean;
  totalPages: number;
  currentPage: number;
  totalItems: number;
  getCurrencySymbol: () => string;
  onSearchChange: (value: string) => void;
  onMinPriceChange: (value: number | "") => void;
  onMaxPriceChange: (value: number | "") => void;
  onClearFilters: () => void;
  onRefresh: () => void;
  onPageChange: (page: number) => void;
  onAddToCart: (id: string) => void;
}

export function ProductGrid({
  filteredInventory,
  products,
  search,
  minPrice,
  maxPrice,
  isLoading,
  inventoryLoading,
  totalPages,
  currentPage,
  totalItems,
  getCurrencySymbol,
  onSearchChange,
  onMinPriceChange,
  onMaxPriceChange,
  onClearFilters,
  onRefresh,
  onPageChange,
  onAddToCart,
}: ProductGridProps) {
  const hasActiveFilters = search !== "" || minPrice !== "" || maxPrice !== "";

  return (
    <div className="flex-1 p-4 md:p-6 overflow-hidden flex flex-col">
      {/* Filtros */}
      <div className="mb-6 space-y-4">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Input
              placeholder="Buscar productos..."
              className="bg-background h-12 text-lg shadow-sm pr-10"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            {search && (
              <button
                onClick={() => onSearchChange("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={onRefresh}
            disabled={inventoryLoading}
            className="h-12 w-12"
          >
            <RefreshCw
              className={`h-5 w-5 ${inventoryLoading ? "animate-spin" : ""}`}
            />
          </Button>

          {hasActiveFilters && (
            <Button variant="outline" onClick={onClearFilters} className="h-12">
              Limpiar
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
              {getCurrencySymbol()}
            </span>
            <Input
              type="number"
              placeholder="Precio desde"
              className="pl-8 h-10"
              value={minPrice}
              onChange={(e) =>
                onMinPriceChange(e.target.value ? Number(e.target.value) : "")
              }
              min="0"
            />
          </div>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
              {getCurrencySymbol()}
            </span>
            <Input
              type="number"
              placeholder="Precio hasta"
              className="pl-8 h-10"
              value={maxPrice}
              onChange={(e) =>
                onMaxPriceChange(e.target.value ? Number(e.target.value) : "")
              }
              min="0"
            />
          </div>
        </div>
      </div>

      {/* Grilla - SIN ScrollArea */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pb-20">
            {filteredInventory.map((item) => {
              const product =
                item.product ||
                products.find((p: any) => p.id === item.productId);
              if (!product) return null;
              return (
                <ProductCard
                  key={item.id}
                  item={item}
                  product={product}
                  onAddToCart={onAddToCart}
                  getCurrencySymbol={getCurrencySymbol}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="mt-6 pt-4 border-t">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ← Anterior
            </Button>
            <span className="text-sm">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Siguiente →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
